import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiFetch from '../../../utils/apiFetch';
import { SectionHead, CardSection } from '../components/ui';
import { FONT } from '../constants';
import { C } from '../styles';
import { authHeaders } from '../helpers';

interface BankAccount {
  bank_name?: string;
  account_masked?: string;
  [key: string]: unknown;
}

type BankStatus = 'disconnected' | 'verifying' | 'verified' | 'locked' | (string & {});

export default function BankAccountSection() {
  const [loading, setLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<BankStatus | null>(null);
  const [account, setAccount] = useState<BankAccount | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiFetch('/api/v1/payroll/bank/status', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setStatus(d.status || 'disconnected');
          setAccount(d.account_masked ? d : null);
        } else {
          setStatus('disconnected');
        }
        setLoading(false);
      })
      .catch(() => { setStatus('disconnected'); setLoading(false); });
  }, []);

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading bank status...</div>;

  const isConnected = Boolean(status && status !== 'disconnected');
  const isVerifying = status === 'verifying';
  const isVerified = status === 'verified';
  const isLocked = status === 'locked';

  return (
    <>
      <SectionHead
        title="Bank account"
        subtitle="Connect your business bank account to send direct deposits automatically from Novala."
      />

      {isConnected ? (
        <>
          <CardSection label="Connected bank account">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '6px 0' }}>
              <div style={{
                width: 46, height: 46, borderRadius: 10,
                background: C.tealSoft, color: C.tealInk,
                display: 'grid', placeItems: 'center',
                flex: '0 0 46px', fontSize: 20,
              }}>🏦</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{account?.bank_name || 'Your bank'}</div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 11, fontWeight: 700, padding: '3px 10px',
                    borderRadius: 5, letterSpacing: '0.04em', textTransform: 'uppercase',
                    background: isVerified ? C.greenSoft : C.amberSoft,
                    color: isVerified ? C.green : C.amber,
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: isVerified ? C.green : C.amber,
                    }} />
                    {isLocked ? 'Locked' : isVerified ? 'Verified' : 'Verifying'}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
                  {account?.account_masked || 'Account details hidden'}
                </div>
              </div>
              <button
                onClick={() => navigate('/payroll/bank/connect')}
                style={{
                  background: '#fff', color: C.ink,
                  border: '1.5px solid ' + C.ink, borderRadius: 8,
                  padding: '8px 14px', fontWeight: 700, fontSize: 12,
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                Change bank
              </button>
            </div>
          </CardSection>

          {isVerifying && (
            <CardSection label="Verify your account">
              <div style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.6, marginBottom: 14 }}>
                We sent a small deposit (less than $1.00) to your account within 1-2 business days. Once you see it, confirm the amount to finish verification.
              </div>
              <button
                onClick={() => navigate('/payroll/bank/verify')}
                style={{
                  background: C.teal, color: '#fff', border: 0, borderRadius: 8,
                  padding: '10px 18px', fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', fontFamily: FONT,
                }}
              >
                Verify now
              </button>
            </CardSection>
          )}

          {isVerified && (
            <CardSection label="Ready to run payroll">
              <div style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.6 }}>
                Your bank is connected and verified. When you finalize a pay run, Novala will send direct deposits from this account.
              </div>
            </CardSection>
          )}
        </>
      ) : (
        <>
          <div style={{
            background: C.tealSoft, borderLeft: '3px solid ' + C.teal,
            borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 18,
          }}>
            <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.6 }}>
              <strong style={{ fontWeight: 700, display: 'block', marginBottom: 3 }}>
                Connect your bank to enable direct deposit
              </strong>
              Direct deposit lets Novala send money straight from your bank to your employees on payday. No more writing cheques or setting up transfers manually.
            </div>
          </div>

          <CardSection label="How it works">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                ['1', 'Connect your bank', 'Link your business chequing account through our secure banking partner. Same tech used by QuickBooks, Wave, and Xero.'],
                ['2', 'Verify your business', 'A quick check confirms you own the business. Required for compliance and fraud protection.'],
                ['3', 'Pay employees with one click', 'On payday, click Pay all. Money moves from your bank to your employees within 1-2 business days.'],
                ['4', 'CRA remittance automated', 'Novala also sends your monthly payroll deductions to CRA on the due date, so you never miss a deadline.'],
              ].map(([n, title, desc]) => (
                <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: C.tealSoft, color: C.tealInk,
                    display: 'grid', placeItems: 'center',
                    fontSize: 15, fontWeight: 700, flexShrink: 0,
                  }}>{n}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.55 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardSection>

          <CardSection label="While you wait">
            <div style={{ fontSize: 13.5, color: C.ink, lineHeight: 1.6, marginBottom: 14 }}>
              You can still run payroll today. Novala calculates everything correctly. You just need to:
            </div>
            <ul style={{ margin: '0 0 14px', paddingLeft: 24, fontSize: 13.5, color: C.ink, lineHeight: 1.7 }}>
              <li>Pay employees manually via cheque, e-transfer, or your bank's online banking</li>
              <li>Add the cheque number in Novala when you write one (for audit trail)</li>
              <li>File PD7A remittance and pay CRA through their online portal</li>
            </ul>
          </CardSection>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={() => navigate('/payroll/bank/connect')}
              style={{
                background: C.teal, color: '#fff', border: 0, borderRadius: 10,
                padding: '12px 22px', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', fontFamily: FONT,
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              Connect your bank →
            </button>
          </div>
        </>
      )}
    </>
  );
}